import type { SqlMonacoContentChangeEvent } from '../../common/utils/sqlMonacoTypes';

/**
 * Keeps the Visual view on the same statement while the buffer changes under
 * it (visual edits, Cmd/Ctrl+Z, a file reload, an MCP/AI write). The anchor
 * is the shown statement's start offset.
 *
 * An edit follows Monaco's own change ranges, so repeated text can never make
 * it guess the wrong side of a change: a change after the anchor leaves it, a
 * change before it moves it by the length delta, an insertion AT the anchor
 * pushes it right (text inserted before the statement keeps the view on the
 * statement; a `WITH …` prepended to it lands inside the grown statement,
 * which snapping turns back into its start), and a replacement that starts at
 * the anchor keeps it there. A replaced span that straddles the anchor
 * (a whole-buffer Format, or its undo) and a whole-document `setValue` (a file
 * reload, an MCP write) have no position to follow, so they keep the
 * statement's ordinal. The result always snaps to a real statement start, so the view
 * never falls back to "statement 1" because the anchor landed in whitespace.
 */
export function rebaseStatementAnchor(
  anchor: number,
  event: SqlMonacoContentChangeEvent,
  previous: string,
  next: string,
  statementStarts: (text: string) => readonly number[],
): number {
  const nextStarts = statementStarts(next);
  const straddles = event.changes.some(
    (change) => change.rangeOffset < anchor && change.rangeOffset + change.rangeLength > anchor,
  );
  if (event.isFlush || straddles) {
    const ordinal = containingIndex(statementStarts(previous), anchor);
    return nextStarts[Math.min(ordinal, nextStarts.length - 1)] ?? 0;
  }
  let delta = 0;
  for (const change of event.changes) {
    // A change after the anchor, or a replacement starting at it, leaves it.
    if (change.rangeOffset > anchor || (change.rangeOffset === anchor && change.rangeLength > 0)) continue;
    delta += change.text.length - change.rangeLength;
  }
  return nextStarts[containingIndex(nextStarts, anchor + delta)] ?? 0;
}

/** Index of the statement containing (or last starting before) `offset`. */
function containingIndex(starts: readonly number[], offset: number): number {
  let index = 0;
  starts.forEach((start, i) => {
    if (start <= offset) index = i;
  });
  return index;
}
